/**
 * @fileoverview The Public switch's first question, in one place.
 *
 * A plant's, a bed's and a season's Public switch each ask the gardener once
 * before they first turn on: the consent, and for a plant the name to show in
 * the gallery. Whether to ask is the server's call, not the browser's (audit
 * A41): GET /shares/plant/:id, /shares/location/:id and /shares/season/:year
 * answer needsConsent and needsGalleryName, so a second phone, or one that
 * never saw the first answer, asks exactly what the first phone did. This hook
 * holds the dialog's open state and the decision; the switch components keep
 * their own mutation and their own dialog.
 *
 * A sharing read that fails on our side is reported to Sentry
 * (reportServerFailure) once; the switch stays hidden or greyed, as it was.
 *
 * Where: PublicPageSwitch (plant and bed) and SeasonReviewPage (season).
 * API: none of its own; it reads the PlantSharing the callers fetch
 * (getSubjectSharing, getSeasonSharing).
 * Settings: none. Words: none.
 * Watch out: the account's public_consent_at copy (mysqlUser) is only kept in
 * step so Profile > Public pages shows the date; nothing decides from it.
 *
 * @module usePublicSwitch
 */

import React from 'react';
import { useAuth } from '@/shared/hooks/useAuth';
import { isServerFailure, reportServerFailure } from '@/shared/lib/serverFailure';
import type { PlantSharing } from '@/shared/api/shares';

/** What a switch component gives the hook. */
interface PublicSwitchOptions {
  /** The server's answer for this switch; undefined until it is read. */
  sharing: PlantSharing | undefined;
  /** Why the sharing read failed, if it did; a failure on our side is reported. */
  readError?: unknown;
  /** Where the failure came from, for the Sentry report. */
  where: string;
  /** Saves the switch; the first time on it also gets the gallery name a plant asked for. */
  apply: (on: boolean, galleryName?: string, onSettled?: () => void) => void;
}

/** What the hook gives back. */
interface PublicSwitchState {
  /** Whether the first-time dialog is open. */
  asking: boolean;
  /** Opens or closes the first-time dialog. */
  setAsking: (open: boolean) => void;
  /** The switch was pressed: asks first when the server says to, else saves at once. */
  press: (on: boolean) => void;
  /**
   * The dialog was confirmed: saves the switch on (with the gallery name a
   * plant asked for) and closes the dialog once the save is done, so the
   * question stays open, its action waiting, until then (plant, bed and season).
   */
  confirm: (galleryName?: string) => void;
  /** A save of the switch worked: keeps the profile's consent date in step. */
  saved: (on: boolean) => void;
}

/**
 * The Public switch's first question, as the server says it.
 *
 * @param options - The server's answer and how to save the switch.
 * @returns The dialog's state and the three moves a switch makes.
 */
export function usePublicSwitch({ sharing, readError, where, apply }: PublicSwitchOptions): PublicSwitchState {
  const { mysqlUser, updateMysqlUser } = useAuth();
  const [asking, setAsking] = React.useState(false);

  React.useEffect(() => {
    if (readError && isServerFailure(readError)) reportServerFailure(readError, where);
  }, [readError, where]);

  /**
   * The switch pressed: the first time on asks before anything is saved, as
   * the server says; every other press saves at once.
   *
   * @param on - The state asked for.
   */
  const press = (on: boolean): void => {
    if (on && sharing && (sharing.needsConsent || sharing.needsGalleryName)) { setAsking(true); return; }
    apply(on);
  };

  /**
   * The first-time dialog confirmed.
   *
   * @param galleryName - A plant's gallery name, from the dialog.
   */
  const confirm = (galleryName?: string): void => {
    apply(true, galleryName, () => setAsking(false));
  };

  /**
   * A save of the switch worked.
   *
   * @param on - The state saved.
   */
  const saved = (on: boolean): void => {
    // The first time is the consent moment; the server stamped it, and the profile shows its date.
    if (on && sharing?.needsConsent && mysqlUser && !mysqlUser.public_consent_at) {
      updateMysqlUser({ public_consent_at: new Date().toISOString() });
    }
  };

  return { asking, setAsking, press, confirm, saved };
}
